import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import Table from '../ogComponent/table';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../ogComponent/spinner';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import dayjs from 'dayjs';

export default function BatchDetails() {
    const [loading, setLoading] = useState(false);
    const [batch, setBatch] = useState();
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({});
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [errors, setErrors] = useState({});
    const { id } = useParams();
    const toast = useRef(null);
    const navigate = useNavigate();

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["deletebatchesID", "editbatchesID", "showstudentdetailsID"];

    useEffect(() => {
        const fetchBatches = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    throw new Error("Access token missing");
                }
                const response = await axios.get(
                    `${process.env.REACT_APP_API}/api/course/batches/${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                const batchData = response.data;
                setBatch(batchData);

                // Parse the start_time and end_time into dayjs objects
                const parsedStartTime = batchData.start_time
                    ? dayjs(batchData.start_time, "hh:mm A")
                    : null;
                const parsedEndTime = batchData.end_time
                    ? dayjs(batchData.end_time, "hh:mm A")
                    : null;
                // Assuming batchData.course is either an ID or an object with an id property
                const courseId = batchData.course?.id || batchData.course || "";
                setFormData({
                    name: batchData.name || "",
                    start_time: parsedStartTime,
                    end_time: parsedEndTime,
                    student_capacity: batchData.student_capacity || "",
                    course_id: courseId,
                });
                setLoading(false);
            } catch (error) {
                console.error("Error fetching batches:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.message || "Failed to fetch batch details",
                    life: 3000,
                });
                setLoading(false);
            }
        };

        fetchBatches();
    }, [id]);

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    throw new Error("Access token missing");
                }
                const response = await axios.get(
                    `${process.env.REACT_APP_API}/api/course/students/?selected_batch=${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                // Map the API response to match table structure
                const mappedStudents = response.data.map((student, index) => ({
                    count: index + 1,
                    name: student.full_name,
                    contact: student.contact_number,
                    fee: student.fee_amount || 'N/A',
                    standerd: student.standard || 'N/A', // Note: 'standerd' is likely a typo for 'standard'
                    status: student.status ? 'Active' : 'Inactive',
                    id: student.id // Adding id for row click navigation
                }));

                setFilteredEmployees(mappedStudents);
            } catch (error) {
                console.error("Error fetching students:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.message || "Failed to fetch students",
                    life: 3000,
                });
            }
        };

        fetchStudents();
    }, [id]);

    useEffect(() => {
        // Fetch courses
        const fetchCourses = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    throw new Error("Access token missing");
                }

                const response = await axios.get(`${process.env.REACT_APP_API}/api/course/combined-courses/`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                setCourses(response.data);
            } catch (error) {
                console.error("Error fetching courses:", error);
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.message || "Failed to fetch courses",
                    life: 3000,
                });
            }
        };

        if (permissions.includes("editbatchesID")) {
            fetchCourses();
        }
    }, [permissions]);

    const handleEditClick = () => {
        if (permissions.includes("editbatchesID")) {
            setShowModal(true);
        }
    };

    const handleDeleteClick = () => {
        if (permissions.includes("deletebatchesID")) {
            setShowDeleteModal(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!permissions.includes("deletebatchesID")) return;

        try {
            const accessToken = localStorage.getItem("accessToken");
            await axios.delete(
                `${process.env.REACT_APP_API}/api/course/batches/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Batch deleted successfully",
                life: 3000,
            });

            setShowDeleteModal(false);
            setTimeout(() => {
                navigate('/user/batches/');
            }, 3000);
        } catch (error) {
            console.error("Error deleting batch:", error);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.message || "Failed to delete batch",
                life: 3000,
            });
            setShowDeleteModal(false);
        }
    };

    const handleRowClick = (item) => {
        if (permissions.includes("showstudentdetailsID")) {
            navigate(`/user/student/${item.id}`);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleTimeChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validateForm = () => {
        let tempErrors = {};
        if (!formData.name) tempErrors.name = "Batch name is required";
        if (!formData.start_time) tempErrors.start_time = "Start time is required";
        if (!formData.end_time) tempErrors.end_time = "End time is required";
        if (!formData.course_id) tempErrors.course_id = "Course is required";
        if (!formData.student_capacity || formData.student_capacity < 1)
            tempErrors.student_capacity = "Valid student capacity is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!permissions.includes("editbatchesID")) return;

        if (!validateForm()) return;

        try {
            const accessToken = localStorage.getItem("accessToken");
            const payload = {
                name: formData.name,
                start_time: formData.start_time.format("HH:mm:ss"),
                end_time: formData.end_time.format("HH:mm:ss"),
                student_capacity: parseInt(formData.student_capacity),
                course_id: formData.course_id,
            };

            const response = await axios.put(
                `${process.env.REACT_APP_API}/api/course/batches/${id}/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setBatch(response.data);
            setShowModal(false);
            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Batch updated successfully",
                life: 3000,
            });
        } catch (error) {
            console.error("Error updating batch:", error);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to update batch",
                life: 3000,
            });
        }
    };

    return (
        <div className="singleDepartmentContainer px-md-5 px-3 pt-4 w-100">
            <Toast ref={toast} position="bottom-right" />
            {batch ? (
                <>
                    <h4 className='text-capitalize'>{batch?.name}</h4>
                    <table>
                        <tr>
                            <td className="ps-0">Course :</td>
                            <td>{batch?.course?.course_name || "N/A"}</td>
                        </tr>
                        <tr>
                            <td className="ps-0">Start Time :</td>
                            <td>{batch?.start_time}</td>
                        </tr>
                        <tr>
                            <td className="ps-0">End Time :</td>
                            <td>{batch?.end_time}</td>
                        </tr>
                        <tr>
                            <td className="ps-0">Students :</td>
                            <td>{batch?.no_of_students || 0}/{batch?.student_capacity || "N/A"}</td>
                        </tr>
                    </table>

                    <div className="employeeTableContainer w-100">
                        <div className="d-flex justify-content-end mb-3 gap-2 gap-md-3">
                            {permissions.includes("editbatchesID") && (
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={handleEditClick}
                                >
                                    <IconEdit stroke={2} size={20} />
                                </button>
                            )}
                            {permissions.includes("deletebatchesID") && (
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={handleDeleteClick}
                                >
                                    <IconTrash stroke={2} size={20} />
                                </button>
                            )}
                        </div>

                        {permissions.includes("deletebatchesID") && showDeleteModal && (
                            <div
                                className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`}
                                tabIndex="-1"
                                style={{ backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent' }}
                            >
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Confirm Deletion</h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setShowDeleteModal(false)}
                                            ></button>
                                        </div>
                                        <div className="modal-body">
                                            <p>Are you sure you want to delete this batch?</p>
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => setShowDeleteModal(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleConfirmDelete}
                                            >
                                                Confirm
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {filteredEmployees.length > 0 ? (
                            <Table
                                header={[
                                    { name: "count", label: "#", active: true },
                                    { name: "name", label: "Name", active: true },
                                    { name: "contact", label: "Number", active: true },
                                    { name: "fee", label: "Fee", active: true },
                                    { name: "standerd", label: "Standerd", active: true },
                                    { name: "status", label: "Status", active: true },
                                    { name: "edit", label: "", active: false }, // Edit column disabled as not used
                                ]}
                                product={filteredEmployees}
                                onRowClick={handleRowClick}
                            />
                        ) : (
                            <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
                                <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                                    backgroundImage: 'url(/assets/file.png)',
                                    backgroundSize: 'contain',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    opacity: 0.3,
                                }}></div>
                                <p className="m-0 fw-light fs-5 position-relative">No Students Found!</p>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <Spinner />
            )}

            {showModal && permissions.includes("editbatchesID") && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Batch</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Batch Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name || ''}
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.name ? "border-danger" : ""}`}
                                        placeholder="Enter batch name"
                                    />
                                    {errors.name && <small className="text-danger">{errors.name}</small>}
                                </div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="mb-3">
                                        <label className="form-label">Start Time</label>
                                        <TimePicker
                                            value={formData.start_time}
                                            onChange={(value) => handleTimeChange("start_time", value)}
                                            ampm={true}
                                            format="hh:mm A"
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth error={!!errors.start_time} />
                                            )}
                                        />
                                        {errors.start_time && <small className="text-danger">{errors.start_time}</small>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">End Time</label>
                                        <TimePicker
                                            value={formData.end_time}
                                            onChange={(value) => handleTimeChange("end_time", value)}
                                            ampm={true}
                                            format="hh:mm A"
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth error={!!errors.end_time} />
                                            )}
                                        />
                                        {errors.end_time && <small className="text-danger">{errors.end_time}</small>}
                                    </div>
                                </LocalizationProvider>
                                <div className="mb-3">
                                    <label className="form-label mb-0 small">Course</label>
                                    <select
                                        className={`form-select ${errors.course_id ? "border-danger" : ""}`}
                                        name="course_id"
                                        value={formData.course_id || ""}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map((course) => (
                                            <option key={course.id} value={course.id}>
                                                {course.course_name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.course_id && <span className="text-danger">{errors.course_id}</span>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Student Capacity</label>
                                    <input
                                        type="number"
                                        name="student_capacity"
                                        value={formData.student_capacity || ''}
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.student_capacity ? "border-danger" : ""}`}
                                        placeholder="Enter student capacity"
                                        min="1"
                                    />
                                    {errors.student_capacity && (
                                        <small className="text-danger">{errors.student_capacity}</small>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSubmit}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}